import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useScrollLock } from "@/lib/scrollLock";
import { getTranslatedText } from "@/lib/translations";
import type { Product } from "@/lib/types";

interface Props {
  open: boolean;
  onClose: () => void;
  productsById: Map<string, Product>;
}

/** Same order as the tablet's `allergen` const (Restaurant.js:95). */
const ALLERGENS = [
  "gluten",
  "arachide",
  "coque",
  "celeri",
  "moutarde",
  "oeuf",
  "lait",
  "poisson",
  "crustace",
  "mollusque",
  "soja",
  "anhydride",
  "sesame",
  "lupin",
] as const;

type AllergenKey = (typeof ALLERGENS)[number];

interface Row {
  id: string;
  name: string;
  flags: Record<AllergenKey, boolean>;
}

export const AllergenModal = ({ open, onClose, productsById }: Props): JSX.Element | null => {
  const { t, i18n } = useTranslation();
  const isCJK = ["cn", "ja", "ko"].includes(i18n.language);
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const rows: Row[] = useMemo(() => {
    if (!open) return [];
    const out: Row[] = [];
    for (const product of productsById.values()) {
      const name = (getTranslatedText(product as any, "name", i18n.language) || "").trim();
      if (!name) continue;
      const list = ((product as any).allergene as string[] | undefined) ?? [];
      const flags = {} as Record<AllergenKey, boolean>;
      for (const a of ALLERGENS) flags[a] = list.includes(a);
      out.push({ id: product.id, name, flags });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name, i18n.language));
  }, [open, productsById, i18n.language]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60]"
      role="dialog"
      aria-modal="true"
      aria-label={t("modalAllergen.title", "Informations sur les allergènes")}
    >
      <button
        type="button"
        aria-label={t("common.close", "Fermer")}
        onClick={onClose}
        className="absolute inset-0 bg-ink/60 animate-fade-in"
      />
      <div className="absolute inset-x-0 top-0 bottom-0 sm:inset-x-auto sm:right-0 sm:max-w-2xl sm:w-full bg-bg flex flex-col animate-slide-up shadow-warm-lg pb-safe">
        <header className="sticky top-0 z-10 bg-bg border-b border-border px-5 pt-5 pb-3 flex items-center justify-between gap-3 pt-safe">
          <h2 className="display-font text-xl font-semibold text-ink leading-tight truncate">
            {t("modalAllergen.title", "Informations sur les allergènes")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-surface shadow-warm-sm flex items-center justify-center text-ink shrink-0"
            aria-label={t("common.close", "Fermer")}
          >
            ✕
          </button>
        </header>

        <div className="flex-1 overflow-auto overscroll-contain">
          {rows.length === 0 ? (
            <div className="p-8 text-center text-ink-soft">—</div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-surface z-[5] shadow-warm-sm">
                <tr>
                  <th className="text-left font-semibold text-ink px-3 py-3 sticky left-0 bg-surface z-[6]">
                    {t("modalAllergen.product", "Produit")}
                  </th>
                  {ALLERGENS.map((a) => (
                    <th
                      key={a}
                      className="font-semibold text-ink-soft px-2 py-3 text-[11px] whitespace-nowrap"
                      style={{
                        writingMode: "vertical-rl",
                        transform: isCJK ? undefined : "rotate(180deg)",
                      }}
                    >
                      {t(`modalAllergen.${a}`, a)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-3 py-2 text-ink sticky left-0 bg-bg">{r.name}</td>
                    {ALLERGENS.map((a) => (
                      <td
                        key={a}
                        className={`px-2 py-2 text-center ${
                          r.flags[a] ? "text-danger font-semibold" : "text-ink-muted"
                        }`}
                        aria-label={
                          r.flags[a]
                            ? t("modalAllergen.contains", {
                                name: t(`modalAllergen.${a}`, a),
                                defaultValue: `Contient ${t(`modalAllergen.${a}`, a)}`,
                              })
                            : ""
                        }
                      >
                        {r.flags[a] ? "✗" : ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-border text-[11px] text-ink-muted bg-surface">
          <p>{t("modalAllergen.bottomFruit", "Pour votre santé, mangez au moins cinq fruits et légumes par jour")}</p>
          <p className="mt-1">{t("modalAllergen.bottomAlcohol", "L'abus d'alcool est dangereux pour la santé, à consommer avec modération")}</p>
        </footer>
      </div>
    </div>,
    document.body
  );
};
